import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, SwapHoriz } from '@mui/icons-material';
import { attendanceAPI, batchesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const STATUSES = [
  { value: 'present', label: 'Present', Icon: CheckCircle, color: '#2e7d32' },
  { value: 'absent', label: 'Absent', Icon: Cancel, color: '#d32f2f' },
  { value: 'makeup', label: 'Makeup', Icon: SwapHoriz, color: '#ed6c02' },
];

const AttendanceMark = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: sessionRes, isLoading: sessionLoading, error: sessionError } = useQuery(
    ['attendanceSession', sessionId],
    () => attendanceAPI.getSessionById(sessionId),
    { enabled: !!sessionId }
  );
  const session = sessionRes?.data?.data;

  const { data: studentsRes, isLoading: studentsLoading } = useQuery(
    ['batchStudents', session?.batch_id],
    () => batchesAPI.getStudents(session?.batch_id),
    { enabled: !!session?.batch_id }
  );
  const batchStudents = studentsRes?.data?.data?.students ?? [];

  const { data: recordsRes } = useQuery(
    ['attendanceRecords', sessionId],
    () => attendanceAPI.getRecordsBySession(sessionId),
    { enabled: !!sessionId }
  );
  const records = recordsRes?.data?.data ?? [];
  const recordByStudent = Object.fromEntries(records.map((r) => [r.student_id, r]));

  const upsertMutation = useMutation(
    (payload) => attendanceAPI.upsertRecord(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attendanceRecords', sessionId]);
        toast.success('Attendance updated');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update attendance');
      },
    }
  );

  const handleStatus = (studentId, status) => {
    upsertMutation.mutate({
      session_id: sessionId,
      student_id: studentId,
      status,
    });
  };

  if (sessionLoading || !session) {
    if (sessionError) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">Session not found.</Typography>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
            Back to dashboard
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (studentsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const batchName = session.batch_name || 'Session';
  const sessionLabel = `${session.session_date} ${session.start_time || ''} – ${session.end_time || ''}`;

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: 'auto',
        px: isMobile ? 2 : 3,
        py: 2,
        pb: 4,
      }}
    >
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
        size={isMobile ? 'medium' : 'large'}
      >
        Back
      </Button>

      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Mark attendance
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        {batchName} · {sessionLabel}
      </Typography>

      {batchStudents.length === 0 ? (
        <Typography color="text.secondary">No students in this batch.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {batchStudents.map((student) => {
            const current = recordByStudent[student.student_id];
            const name = student.student_name || `Student ${student.student_id?.slice(0, 8)}`;

            return (
              <Card
                key={student.student_id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ py: isMobile ? 2 : 2.5, px: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    sx={{ mb: 1.5, fontSize: isMobile ? '1rem' : '1.1rem' }}
                  >
                    {name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      '& .MuiButton-root': {
                        minHeight: 44,
                        minWidth: isMobile ? 100 : 110,
                        flex: isMobile ? '1 1 30%' : 'none',
                        fontSize: isMobile ? '0.95rem' : '1rem',
                      },
                    }}
                  >
                    {STATUSES.map(({ value, label, Icon, color }) => {
                      const isSelected = current?.status === value;
                      return (
                        <Button
                          key={value}
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="large"
                          startIcon={<Icon sx={{ fontSize: 22 }} />}
                          onClick={() => handleStatus(student.student_id, value)}
                          disabled={upsertMutation.isLoading}
                          sx={{
                            bgcolor: isSelected ? color : undefined,
                            borderColor: isSelected ? color : undefined,
                            '&:hover': {
                              bgcolor: isSelected ? color : undefined,
                              borderColor: color,
                              borderWidth: 2,
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                  {current && (
                    <Chip
                      label={`Saved: ${current.status}`}
                      size="small"
                      sx={{ mt: 1.5 }}
                      color="default"
                      variant="outlined"
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AttendanceMark;
